import { SelectPreviousCertificationService } from '@aca-new/app/pages/book/pages/food-certification/shared/components/food-general-information/shared/components/select-previous-certification/shared/services/select-previous-certification.service';
import { IDraft } from '@aca-new/app/pages/book/shared/models/interfaces/draft.interface';
import { BookRouteType } from '@aca-new/app/pages/book/shared/models/types/book-route.type';
import { BookingFeatureService } from '@aca-new/app/pages/book/shared/services/booking-feature.service';
import { BookingsService } from '@aca-new/app/pages/bookings/shared/services/bookings.service';
import { IPreviousOrder } from '@aca-new/app/pages/main/shared/components/aside/shared/components/aside-menu/share/models/interfaces/previous-order.interface';
import { AsideService } from '@aca-new/app/pages/main/shared/components/aside/shared/services/aside.service';
import { PAYMENT_ITEMS_QUERY_PARAM } from '@aca-new/app/pages/payment-checkout/shared/functions/payment-checkout-order-fee.function';
import { EPayFrom } from '@aca-new/app/pages/payment-checkout/shared/models/enums/pay-from.enum';
import { PayByTransferService } from '@aca-new/app/pages/payments/shared/components/pay-by-transfer/shared/services/pay-by-transfer.service';
import { LOGROCKET_PERF_EVENTS, LOGROCKET_PERF_SESSION_KEYS } from '@aca-new/app/shared/constants/logrocket-performance.constants';
import { IPostMessageData } from '@aca-new/app/shared/interfaces/post-message.interface';
import { EAvailablePath } from '@aca-new/app/shared/models/enums/available-path.enum';
import { ELocalForageKeys } from '@aca-new/app/shared/models/enums/localforage-keys.enum';
import { EPostMessageType } from '@aca-new/app/shared/models/enums/post-message-type.enum';
import { EServiceType } from '@aca-new/app/shared/models/enums/service-type.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { AppLocationService } from '@aca-new/app/shared/services/browser-services/app-location/app-location-service';
import { LogRocketPerformanceService } from '@aca-new/app/shared/services/logrocket-performance/logrocket-performance.service';
import { AppOverlayService } from '@aca-new/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { AppPostMessageService } from '@aca-new/app/shared/services/post-message-services/app-post-message/app-post-message.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { ENVIRONMENT } from '@aca-new/environments/environment';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { qimaEnumValuesToArray } from '@qima/ngx-qima';
import localforage from 'localforage';
import { defer, Observable, of, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';

declare let ENV_TYPE: 'dev' | 'pp' | 'prod';

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class AcaMessageService {
  public isAcaFrameAvailable = true;
  public isAcaFrameRendered = false;

  private readonly _companyLogoSubject$: Subject<void> = new Subject<void>();
  private readonly _acaUrl = this._appLocationService.getAcaUrl();
  private readonly _messageSubjects = new Map<EPostMessageType, Subject<IPostMessageData>>();

  public constructor(
    private readonly _appAuthenticationService: AppAuthenticationService,
    private readonly _appPostMessageService: AppPostMessageService,
    private readonly _appLocationService: AppLocationService,
    private readonly _bookingsService: BookingsService,
    private readonly _router: Router,
    private readonly _overlayService: AppOverlayService,
    private readonly _asideService: AsideService,
    private readonly _sessionStorage: StorageService,
    private readonly _payByTransferService: PayByTransferService,
    private readonly _storageService: StorageService,
    private readonly _translateService: TranslateService,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _bookingFeatureService: BookingFeatureService,
    private readonly _selectPreviousCertificationService: SelectPreviousCertificationService,
    private readonly _logRocketPerformance: LogRocketPerformanceService,
    @Inject(WINDOW) private readonly _window: Window,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  public getAcaFrameUrl(url: Readonly<string>): string {
    if (!this._shouldRenderAcaFrame()) {
      return '';
    }

    let validUrl = this._appLocationService.getAcaUrl() + this._cleanUrl(url);

    if (/step-\d+/.test(validUrl)) {
      /**
       * Why we need to replace the "step-${number}" to "step%3D${number}"?
       * On aca side, the path is 'https://aca.qima.com/${router}step=${number}'
       * On MyQIMA side, the '=' is not available in the url, so we use '-' instead.
       * When we want to navigate to aca, we need to replace the '-' to '%3D' which is encoded from '='.
       */
      const pathParams = /step-\d+/.exec(validUrl)?.[0] as string;
      const validPathParams = pathParams.replace('-', '%3D');

      validUrl = validUrl.replace(pathParams, validPathParams);
    }

    return validUrl;
  }

  /**
   * Emits once when ACA iframe has rendered (ready for postMessage).
   * If ACA has already rendered, emits immediately; otherwise waits for acaFrameRendered from ACA.
   * @returns {Observable<void>} Emits when ACA is ready.
   */
  public getWhenAcaReady$(): Observable<void> {
    return defer(
      (): Observable<void> =>
        this.isAcaFrameRendered
          ? of(undefined)
          : this.registerMessageHandler$(EPostMessageType.ACA_FRAME_RENDERED).pipe(
              take(1),
              map((): void => undefined)
            )
    );
  }

  public isMyQimaPageAvailable(path: string): boolean {
    if (!this._shouldRenderAcaFrame()) {
      return true;
    }

    let availablePaths = qimaEnumValuesToArray(EAvailablePath);

    if (!this._appUserSettingService.getIsTestCompany()) {
      availablePaths = availablePaths.filter(
        (availablePath: EAvailablePath): boolean =>
          !availablePath.startsWith('/book') || availablePath.startsWith(EAvailablePath.BOOKINGS) || availablePath.startsWith(EAvailablePath.BOOK_FOOD_CERTIFICATION)
      );
    }

    return availablePaths.some((availablePath: EAvailablePath): boolean => path.startsWith(availablePath));
  }

  public canAcaRouteTo(path: string): boolean {
    const paths = [
      EAvailablePath.NETWORK,
      EAvailablePath.PRODUCE_REPORTS,
      EAvailablePath.SUPPLIER_DOCUMENTS,
      EAvailablePath.BOOK_FOOD_CERTIFICATION,
      EAvailablePath.FACILITIES,
      EAvailablePath.FIELDS,
      EAvailablePath.PRODUCER,
      EAvailablePath.PRODUCERS,
      EAvailablePath.SHIPPER,
      EAvailablePath.SHIPPERS,
    ];

    return !paths.some((availablePath: EAvailablePath): boolean => path.startsWith(availablePath));
  }

  public watchMessageFromAca(): void {
    this._appPostMessageService.addHandler('acaMessager', (event): void => {
      if (this._acaUrl.includes(event.origin)) {
        this._messageHandler(event.data as IPostMessageData);
      }
    });
  }

  public pathChangeHandler(target: string, action: string = ''): void {
    let isFromMyQima = false;
    let isTargetMyQima = false;

    qimaEnumValuesToArray(EAvailablePath).forEach((path): void => {
      if (this._router.url.startsWith(path)) {
        isFromMyQima = true;
      }

      if (target.startsWith(path)) {
        isTargetMyQima = true;
      }
    });

    if (isFromMyQima && isTargetMyQima) {
      void this._router.navigateByUrl(target);
    } else {
      this.postMessageToAca({ type: EPostMessageType.PATH_CHANGE, data: { path: target, action } });
    }
  }

  public postMessageToAca(message: IPostMessageData): void {
    const contentWindow = this._getAcaFrame()?.contentWindow;

    if (contentWindow) {
      this._maybeBeginBookInspectionAcaIframePerf(message);
      this._maybeBeginViewOrderAcaIframePerf(message);
      this._appPostMessageService.post(contentWindow, message, this._acaUrl);
    }
  }

  /**
   * @description
   * Whether MyQIMA can post to a live ACA iframe. `isAcaFrameRendered` alone is
   * not enough: routes outside MainComponent (e.g. interactive-report-v2) destroy
   * `#acaFrame` while the flag can stay true after SPA navigation from Main.
   * @returns {boolean} True when the ACA iframe is in the DOM and was marked rendered.
   */
  public canPostMessageToAca(): boolean {
    return this.isAcaFrameRendered && !!this._getAcaFrame()?.contentWindow;
  }

  public companyLogoChange$(): Observable<void> {
    return this._companyLogoSubject$.asObservable();
  }

  public registerMessageHandler$(type: EPostMessageType): Observable<IPostMessageData> {
    const existingSubject$: Subject<IPostMessageData> | undefined = this._messageSubjects.get(type);

    if (existingSubject$) {
      return existingSubject$.asObservable();
    }

    const createdSubject$: Subject<IPostMessageData> = new Subject<IPostMessageData>();

    this._messageSubjects.set(type, createdSubject$);

    return createdSubject$.asObservable();
  }

  /**
   * @description
   * Handles navigation between different booking progress routes (ACA and MyQIMA) with the following logic:
   * 1. When both current and target routes are MyQIMA book progress:
   *    - Direct navigation occurs, handled by route guards
   *    - No message sent to ACA
   *
   * 2. When both current and target routes are ACA book progress:
   *    - Sends target route to ACA, wait for ACA to navigate to the target route
   *
   * 3. When current route is ACA book progress and target is MyQIMA book progress:
   *    - Caches target route in sessionStorage (why not use localStorage? to prevent use by other tabs)
   *    - Sends message to ACA to navigate to home page
   *    - When user confirms leaving ACA booking flow, message handler will respond to the message and clear the cache:
   *      If received message is not home page, clears cache
   *      If received message is home page and cache exists, navigates to cached target route
   *
   * 4. When current route is MyQIMA book progress and target is ACA book progress:
   *    - Caches target route in sessionStorage
   *    - Navigates to home page to trigger deactivate Guard dialog
   *    - When user confirms leaving MyQIMA booking flow:
   *      If cache exists, sends message to ACA to navigate to target route
   *
   * 5. For other route combinations:
   *    - If target is ACA route, sends path change message to ACA
   *    - If target is MyQIMA route, performs direct navigation
   * @param {Readonly<string>} path the target route
   * @private
   */
  public bookProgressHandler(path: Readonly<string>): void {
    this._storageService.removeItem(EStorageKeys.SELECTED_LEGAL_ENTITY);
    const myQimaBookServices: EAvailablePath[] = this._bookingFeatureService.getAvailableMyQimaBookingPaths();
    let currentRouteType: BookRouteType = this._router.url.includes('/book-') && !this._router.url.startsWith('/book-confirmation') ? 'aca' : 'other';
    let targetRouteType: BookRouteType = 'aca';

    myQimaBookServices.forEach((service): void => {
      if (this._router.url.includes(service)) {
        currentRouteType = 'myQima';
      }

      if (path.includes(service)) {
        targetRouteType = 'myQima';
      }
    });

    if (currentRouteType === 'aca') {
      if (targetRouteType === 'aca') {
        this.postMessageToAca({ type: EPostMessageType.PATH_CHANGE, data: { path } });
      } else if (targetRouteType === 'myQima') {
        this._sessionStorage.setItem(EStorageKeys.TARGET_BOOK_ROUTE, path);
        this.postMessageToAca({ type: EPostMessageType.PATH_CHANGE, data: { path: '/home' } });
      }
    } else if (currentRouteType === 'other') {
      if (targetRouteType === 'aca') {
        this.postMessageToAca({ type: EPostMessageType.PATH_CHANGE, data: { path } });
      } else if (targetRouteType === 'myQima') {
        void this._router.navigateByUrl(path);
      }
    } else {
      if (targetRouteType === 'aca') {
        this._sessionStorage.setItem(EStorageKeys.TARGET_BOOK_ROUTE, path);
        void this._router.navigateByUrl('/', { skipLocationChange: true }).then((canLeave: boolean): void => {
          if (canLeave) {
            this.postMessageToAca({ type: EPostMessageType.PATH_CHANGE, data: { path } });
          }
        });
      } else if (targetRouteType === 'myQima') {
        void this._router.navigateByUrl('/', { skipLocationChange: true }).then((canLeave: boolean): void => {
          if (canLeave) {
            void this._router.navigateByUrl(path);
          }
        });
      }
    }
  }

  /**
   * When My QIMA asks the ACA iframe to open view-order (inspection or generic), start the same LogRocket
   * view-order perf pair as native My QIMA (`lr_perf_view_order`); legacy ACA completes in `review-controller.js`.
   * @param {IPostMessageData} message Outbound postMessage payload to ACA.
   * @returns {void} Nothing.
   */
  private _maybeBeginViewOrderAcaIframePerf(message: IPostMessageData): void {
    if (message.type !== EPostMessageType.PATH_CHANGE) {
      return;
    }

    const path: unknown = message.data?.path;

    if (typeof path !== 'string') {
      return;
    }

    const normalized: string = path.replace(/^\/aca/i, '');
    const isViewOrderInspection: boolean = normalized === '/view-order-inspection' || normalized.endsWith('/view-order-inspection');
    const isViewOrderGeneric: boolean = normalized === '/view-order';

    if (!isViewOrderInspection && !isViewOrderGeneric) {
      return;
    }

    const params: Record<string, unknown> | undefined = message.data?.params as Record<string, unknown> | undefined;
    const orderIdRaw: unknown = params?.id;
    let orderId: string | undefined;

    if (typeof orderIdRaw === 'string') {
      orderId = orderIdRaw;
    } else if (typeof orderIdRaw === 'number') {
      orderId = orderIdRaw.toString();
    }

    if (!orderId) {
      return;
    }

    const subTypeRaw: unknown = params?.subType;
    const serviceType: string | undefined = typeof subTypeRaw === 'string' ? subTypeRaw.toLowerCase() : undefined;

    this._logRocketPerformance.cancelPendingFlow(LOGROCKET_PERF_SESSION_KEYS.viewOrder);
    this._logRocketPerformance.beginWithStorage(LOGROCKET_PERF_SESSION_KEYS.viewOrder, LOGROCKET_PERF_EVENTS.openBookingsViewOrderClicked, {
      orderId,
      ...(serviceType ? { serviceType } : {}),
      route: 'aca_iframe_shell',
    });
  }

  /**
   * When My QIMA asks the ACA iframe to open a book-inspection path, start a paired LogRocket perf flow;
   * legacy ACA completes it on first `book-inspection.*-step-*` state (see `inspections.route.js` in aca).
   * @param {IPostMessageData} message Outbound postMessage payload to ACA.
   * @returns {void} Nothing.
   */
  private _maybeBeginBookInspectionAcaIframePerf(message: IPostMessageData): void {
    if (message.type !== EPostMessageType.PATH_CHANGE) {
      return;
    }

    const path: unknown = message.data?.path;

    if (typeof path !== 'string' || !path.includes('/book-inspection/')) {
      return;
    }

    const serviceType: string | undefined = this._parseBookInspectionServiceTypeFromPath(path);

    if (!serviceType) {
      return;
    }

    this._logRocketPerformance.cancelPendingFlow(LOGROCKET_PERF_SESSION_KEYS.bookInspectionAcaIframe);
    this._logRocketPerformance.beginWithStorage(LOGROCKET_PERF_SESSION_KEYS.bookInspectionAcaIframe, LOGROCKET_PERF_EVENTS.bookInspectionOpenInAcaClicked, {
      serviceType,
      route: 'aca_iframe_shell',
      entryPath: path.length > 160 ? path.slice(0, 160) : path,
    });
  }

  /**
   * @param {string} path My QIMA or hybrid URL segment containing `/book-inspection/{type}/`.
   * @returns {string | undefined} Lowercase service type segment (e.g. psi, dupro) when present.
   */
  private _parseBookInspectionServiceTypeFromPath(path: string): string | undefined {
    const normalized: string = path.replace(/^\/aca/i, '');
    const match: RegExpExecArray | null = /\/book-inspection\/([^/]+)/i.exec(normalized);

    if (!match?.[1]) {
      return undefined;
    }

    return match[1].toLowerCase();
  }

  private _shouldRenderAcaFrame(): boolean {
    const isDashBoardOnly = this._appUserSettingService.getIsSuperMaster() && this._appUserSettingService.getAccessDashboardOnly();
    const isProduceUser = this._appUserSettingService.getIsQimaProduce();
    const isLifeScienceUser = this._appUserSettingService.getIsLifeScience();

    this.isAcaFrameAvailable = !isDashBoardOnly && !isProduceUser && !isLifeScienceUser;

    return this.isAcaFrameAvailable;
  }

  private _cleanUrl(url: string): string {
    const replacements = [
      { pattern: /interactive-report\/.*/g, replacement: '' },
      { pattern: /supplier-confirmation-hub\/.*/g, replacement: '' },
      { pattern: /supplier-confirmation\/.*/g, replacement: '' },
      { pattern: /entity(\/.*)?/, replacement: '' },
      { pattern: /reflow-report\/.*/g, replacement: '' },
      { pattern: /payments\/checkout\/.*/g, replacement: '' },
      /**
       * Payments pages are handled in MyQIMA.
       * Prevent the hidden ACA iframe from loading ACA payments (which embeds payments-for-aca again).
       */
      { pattern: /payments\/.*/g, replacement: '' },
      { pattern: /payments\/?$/, replacement: '' },
      { pattern: /account\/supplier-sites\/?$/, replacement: '' },
      { pattern: /account\/my-documents\/?$/, replacement: '' },
      /**
       * Production monitoring is available in MyQIMA
       * Prevent MyQIMA page from being redirected to ACA page when browser is refreshed
       */
      { pattern: /book-inspection\/pm\/.*/g, replacement: '' },
      { pattern: /book-inspection\/sp\/.*/g, replacement: '' },
      { pattern: /book-inspection\/dupro\/.*/g, replacement: '' },
      { pattern: /book-food-certification\/brcgs\/.*/g, replacement: '' },
      { pattern: /book-food-certification\/sqf\/.*/g, replacement: '' },
      { pattern: /book-food-certification\/ifs\/.*/g, replacement: '' },
      { pattern: /book-food-certification\/ggap\/.*/g, replacement: '' },
      { pattern: /field\/.*/g, replacement: '' },
      { pattern: /field\/?$/, replacement: 'fields' },
      { pattern: /facility\/?$/, replacement: 'facilities' },
      { pattern: /facility\/.*/g, replacement: '' },
      /**
       * Bookings view order with production monitoring should be handled in MyQIMA
       * Prevent MyQIMA page from being redirected to ACA page when browser is refreshed
       */
      { pattern: /bookings\/view-order\/.*\/pm/g, replacement: '' },
      { pattern: /bookings\/view-order\/.*\/dupro/g, replacement: '' },
      { pattern: /bookings\/view-order\/.*\/ggap/g, replacement: '' },
      { pattern: /bookings\/view-order\/.*\/brcgs/g, replacement: '' },
      { pattern: /bookings\/view-order\/.*\/sqf/g, replacement: '' },
      { pattern: /bookings\/view-order\/.*\/ifs/g, replacement: '' },
      /**
       * Non-conformances pages are handled in MyQIMA
       * Prevent MyQIMA page from being redirected to ACA page when browser is refreshed
       */
      { pattern: /non-conformances\/.*/g, replacement: '' },
      { pattern: /non-conformances\/?$/, replacement: '' },
      { pattern: /home\/?$/, replacement: '' },
      { pattern: /produce-reports\/?$/, replacement: '' },
      { pattern: /network\/?$/, replacement: '' },
      { pattern: /network\/.*/g, replacement: '' },
      { pattern: /book-food-certification\/?$/, replacement: '' },
      { pattern: /book-food-certification\/.*/g, replacement: '' },
      { pattern: /supplier-documents\/?$/, replacement: '' },
      { pattern: /supplier-documents\/add-external\/?$/, replacement: '' },
      { pattern: /supplier\/?$/, replacement: 'suppliers' },
      { pattern: /supplier\/.*/g, replacement: '' },
      { pattern: '/aca/', replacement: '/' },
      { pattern: 'analytics', replacement: 'dashboard' },
    ];

    return replacements.reduce((acc, { pattern, replacement }): string => {
      return acc.replace(pattern, replacement);
    }, url);
  }

  private _getAcaFrame(): HTMLIFrameElement {
    return this._document.getElementById('acaFrame') as HTMLIFrameElement;
  }

  private _paymentModalShow(data: Record<string, unknown>): void {
    if (!data.orderId) {
      return;
    }

    const queryParams: Record<string, string> = { payFrom: EPayFrom.BOOK, serviceType: data.serviceType as string };
    const orderNumber: string = typeof data.orderNumber === 'string' ? data.orderNumber.trim() : '';

    if (orderNumber.length > 0) {
      queryParams[PAYMENT_ITEMS_QUERY_PARAM] = orderNumber;
    }

    void this._router.navigate([`/payments/checkout/${data?.orderId as string}`], { queryParams });
  }

  private _pathChange(data: Record<string, unknown>): void {
    if (!this.isAcaFrameAvailable) {
      return;
    }

    try {
      let path = data.path as string;

      /**
       * Do not navigate to the book confirmation page
       * because it needs to be open the payment checkout page
       */
      if (path?.includes('/aca/book-confirmation/') || path?.includes('/aca/booking-confirmation/')) {
        if (path.includes('canNavigate=true')) {
          const newPath = path
            .replace('/aca', '')
            .replace('?canNavigate=true', path.includes('bookingId=') ? '?' : '')
            .replace('booking-confirmation', 'book-confirmation');

          void this._router.navigateByUrl(newPath);
        }

        return;
      }

      /**
       * Why we need to replace the "step=${number}" to "step-${number}"?
       * On aca side, the path is 'https://aca.qima.com/${router}step=${number}'
       * On MyQIMA side, the '=' is not available in the url, so we use '-' instead.
       * When we want to navigate in MyQIMA, we need to replace the '=' to '-'.
       */
      if (path && /step=\w+/.test(path)) {
        const pathParams = /step=\w+/.exec(path)?.[0] as string;
        const validPathParams = pathParams.replace('=', '-');

        path = path.replace(pathParams, validPathParams);
      }

      if (path.startsWith('/aca/login')) {
        this._window.location.href = `${this._acaUrl}${path.replace('/aca', '')}`;
      }

      if (path.startsWith('/aca/dashboard')) {
        path = path.replace('/aca/dashboard', '/analytics');
      }

      if (path.startsWith('/aca/suppliers/new') || path.startsWith('/aca/suppliers/') || path.includes('/reports?')) {
        return;
      }

      if (path.startsWith('/aca') && this.isMyQimaPageAvailable(path.split('/aca')[1])) {
        path = path.replace('/aca', '').replace('.', '/');
      }

      if (path.startsWith('/aca/logout')) {
        if (this._window.location.origin.includes('localhost')) {
          this._window.location.href = `${this._acaUrl}/login`;
        } else {
          this._window.location.href = `${ENVIRONMENT[ENV_TYPE].MARKETING_WEBSITE_URL}/logout`;
        }

        this._appAuthenticationService.clearAuthenticationData();

        return;
      }

      if (path === '/home') {
        const cachedTargetBookRoute = this._storageService.getItem(EStorageKeys.TARGET_BOOK_ROUTE);
        const cachedPreviousOrderData = this._storageService.getItem(EStorageKeys.TARGET_BOOK_PREVIOUS);

        if (cachedTargetBookRoute) {
          void this._router.navigateByUrl(cachedTargetBookRoute);
          this._storageService.removeItem(EStorageKeys.TARGET_BOOK_ROUTE);

          return;
        } else if (cachedPreviousOrderData) {
          const data = JSON.parse(cachedPreviousOrderData) as IPreviousOrder;

          this._storageService.removeItem(EStorageKeys.TARGET_BOOK_PREVIOUS);

          if ([EServiceType.PM, EServiceType.DUPRO].includes(data.originData.serviceTypeText as EServiceType)) {
            this._bookingsService
              .createDraftFromPreviousOrder$(data.originData.orderId, data.originData.serviceTypeText.toLocaleLowerCase())
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (response: IDraft | null): void => {
                  if (response) {
                    void this._router.navigateByUrl('/', { skipLocationChange: true }).then((canLeave: boolean): void => {
                      if (canLeave) {
                        void this._router.navigate([`/book-inspection/${data.originData.serviceTypeText.toLocaleLowerCase()}/${response.draftId}`]);
                      }
                    });
                  }
                },
                complete: (): void => {
                  this._asideService.updateAsideMenuVisible(false);
                  this._overlayService.updateIsLoading(false);
                },
              });

            return;
          }
        }

        if (this._router.url.split(/[?#]/)[0].startsWith('/supplier-confirmation-hub')) {
          return;
        }
      }

      this._storageService.removeItem(EStorageKeys.TARGET_BOOK_ROUTE);

      void this._router.navigateByUrl(path);
    } catch (e) {
      console.error(e);
    }
  }

  private _languageChange(data: Record<string, unknown>): void {
    const language = data.language as string;

    void localforage.setItem(ELocalForageKeys.LOCALE_LANGUAGE, language);
    this._translateService.use(language).pipe(untilDestroyed(this)).subscribe();
  }

  private _informAcaLanguageChange(): void {
    void localforage.getItem(ELocalForageKeys.LOCALE_LANGUAGE).then((value): void => {
      if (value) {
        this.postMessageToAca({
          type: EPostMessageType.LANGUAGE_CHANGE,
          data: { language: value },
        });
      }
    });
  }

  private _messageHandler(data: IPostMessageData): void {
    // ACA posts its boot route (e.g. `/home` when the iframe URL was stripped) before `acaFrameRendered`; never follow it.
    if (data.type !== EPostMessageType.ACA_FRAME_RENDERED && !this.isAcaFrameRendered) {
      return;
    }

    switch (data.type) {
      case EPostMessageType.PATH_CHANGE:
        this._updateTitle(data.data as Record<string, string>);
        this._pathChange(data.data as Record<string, unknown>);
        break;
      case EPostMessageType.LANGUAGE_CHANGE:
        this._languageChange(data.data as Record<string, unknown>);
        break;
      case EPostMessageType.PAYMENT_MODAL_SHOW:
        this._paymentModalShow(data.data as Record<string, unknown>);
        break;
      case EPostMessageType.PAY_BY_TRANSFER_SHOW:
        this._payByTransferService.openPayByTransfer(data.data as Record<string, unknown>);
        break;
      case EPostMessageType.SELECT_PREVIOUS_CERTIFICATION_SHOW:
        this._selectPreviousCertificationService.onSelectPreviousCertificationShow((data.data as Record<string, string>).draftId as string);
        break;
      case EPostMessageType.LOAD:
        /**
         * Prevent infinite reload loop by skipping reload on v2/login / v2/demologin pages
         * Issue: When MyQIMA receives RELOAD message and reloads, it sends LOAD message to ACA after reload.
         * ACA then responds with another RELOAD message, causing infinite reload cycle.
         * Solution: Skip reload on login routes to break the infinite loop.
         */
        if (!this._router.url.includes('v2/login') && !this._router.url.includes('v2/demologin')) {
          this._informAcaLanguageChange();
        }

        break;
      case EPostMessageType.RELOAD:
        this._window.location.reload();
        break;
      case EPostMessageType.COMPANY_LOGO_CHANGE:
        this._companyLogoSubject$.next();
        break;
      case EPostMessageType.ACA_FRAME_RENDERED:
        this._updateTitle(data.data as Record<string, string>);
        this.isAcaFrameRendered = true;
        break;
    }

    const subject$ = this._messageSubjects.get(data.type as EPostMessageType);

    if (subject$) {
      subject$.next(data);
    }
  }

  private _updateTitle(data: Record<string, string>): void {
    if (!data?.title) {
      return;
    }

    if (!this._window.location.href.includes('/aca/')) {
      return;
    }

    this._document.title = data.title;
  }
}
